import { AnimatePresence, motion } from 'framer-motion'
import useMediaQuery from 'hooks/useMediaQuery'
import { CreditCard, Plus, Receipt, Wallet } from 'lucide-react'
import React from 'react'
import { MOBILE_WIDTH } from 'utils/constants'
import { useOrderB2c } from 'hooks/useOrderB2c'
import { usePagaleveEligibility } from 'hooks/usePagaleveEligibility'
import * as S from './styles'

interface PaymentMethodSelectorProps {
  selectedMethod: string | null
  onSelectMethod: (method: string) => void
  totalAmount: number
  disabledPix?: boolean
  disabledCreditCard?: boolean
  disabledBoleto?: boolean
  hasMultipleMethods?: boolean
  applyPixDiscountOrder?: boolean
}

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  selectedMethod,
  onSelectMethod,
  totalAmount,
  disabledPix,
  disabledCreditCard,
  disabledBoleto,
  hasMultipleMethods,
  applyPixDiscountOrder
}) => {
  const isMobile = useMediaQuery(MOBILE_WIDTH)
  const { person, orderCheckout } = useOrderB2c()

  const applyPixDiscount = process.env.REACT_APP_APPLY_PIX_DISCOUNT === 'enabled' && applyPixDiscountOrder
  const pixDiscountPercentage = Number(process.env.REACT_APP_PIX_DISCOUNT_PERCENTAGE)

  const checkoutPerson = (orderCheckout as any)?.person || {}
  const cpf = checkoutPerson?.cpf || person?.cpf || ''
  const phone = checkoutPerson?.phone || person?.phone || ''
  const email = checkoutPerson?.email || person?.email || ''
  const hasOrderData = Boolean(cpf && totalAmount)

  const { isEligible: isPagaleveEligible, isChecking: isCheckingPagaleve } = usePagaleveEligibility({
    cpf,
    amount: Number(totalAmount) || 0,
    phone,
    email,
    enabled: hasOrderData,
  })

  const singleMethods = [
    {
      id: 'credit_card',
      title: 'Cartão de Crédito',
      icon: <CreditCard size={24} />,
      description: 'Pague com seu cartão de crédito',
      disabled: disabledCreditCard
    },
    {
      id: 'pix',
      title: 'Pix',
      icon: <Wallet size={24} />,
      description: 'Pagamento instantâneo',
      disabled: disabledPix
    },
    // PIX Parcelado (Pagaleve) - aparece somente quando elegível
    ...(isCheckingPagaleve || isPagaleveEligible
      ? [
          {
            id: 'pagaleve',
            title: 'PIX Parcelado',
            icon: <Wallet size={24} />,
            description: 'Pague via Pagaleve em parcelas no PIX',
            disabled: !isPagaleveEligible || disabledPix,
          } as const,
        ]
      : []),
    {
      id: 'boleto',
      title: 'Boleto Bancário',
      icon: <Receipt size={24} />,
      description: 'Vencimento em 3 dias úteis',
      disabled: disabledBoleto
    }
  ]

  const combinedMethods = [
    {
      id: 'credit_card_credit_card',
      title: 'Dois Cartões',
      icon: <CreditCard size={24} />,
      secondIcon: <CreditCard size={24} />,
      description: 'Divida entre dois cartões',
      disabled: disabledCreditCard
    },
    {
      id: 'credit_card_pix',
      title: 'Cartão + Pix',
      icon: <CreditCard size={24} />,
      secondIcon: <Wallet size={24} />,
      description: 'Parte no cartão, parte no Pix',
      disabled: disabledCreditCard || disabledPix
    },
    {
      id: 'credit_card_boleto',
      title: 'Cartão + Boleto',
      icon: <CreditCard size={24} />,
      secondIcon: <Receipt size={24} />,
      description: 'Parte no cartão, parte no boleto',
      disabled: disabledCreditCard || disabledBoleto
    }
  ]

  const applyPaymentDiscount = (amount: number, method: string) => {
    if (applyPixDiscount && method === 'pix') {
      const discount = amount * (pixDiscountPercentage / 100)
      return amount - discount
    }
    return amount
  }


  return (
    <S.PaymentSelectorContainer>
      <S.PaymentSectionTitle>Escolha como deseja pagar</S.PaymentSectionTitle>

      <S.PaymentOptionsSection>
        <S.PaymentOptionsGroup>
          <S.GroupTitle>Pagamento único</S.GroupTitle>
          <S.OptionsRow>
            {singleMethods.map((method) => (
              <S.PaymentOption
                key={method.id}
                isSelected={selectedMethod === method.id}
                onClick={() => onSelectMethod(method.id)}
                disabled={method.disabled}
                isMobile={isMobile}>
                <div className="icon-container">{method.icon}</div>
                <div className="method-info">
                  <h3>{method.title}</h3>
                  <p>
                    {method.id === 'pagaleve' && isCheckingPagaleve ? 'Verificando elegibilidade...' : method.description}
                  </p>
                </div>
                <div className="amount">
                  {applyPixDiscount && method.id === 'pix' && (
                    <div className="discount-container">
                      <span className="discount-label">-{pixDiscountPercentage}%</span>
                      <span className="discount-value">
                        {new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL'
                        }).format(totalAmount)}
                      </span>
                    </div>
                  )}
                  {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                  }).format(applyPaymentDiscount(totalAmount, method.id))}
                </div>
                <AnimatePresence>
                  {selectedMethod === method.id && (
                    <motion.div
                      className="check-indicator"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                    />
                  )}
                </AnimatePresence>
              </S.PaymentOption>
            ))}
          </S.OptionsRow>
        </S.PaymentOptionsGroup>

        {hasMultipleMethods && (
          <S.PaymentOptionsGroup>
            <S.GroupTitle>Pagamento combinado</S.GroupTitle>
            <S.OptionsRow>
              {combinedMethods.map((method) => (
                <S.PaymentOption
                  key={method.id}
                  isSelected={selectedMethod === method.id}
                  onClick={() => onSelectMethod(method.id)}
                  disabled={method.disabled}
                  isMobile={isMobile}>
                  <div className="combined-icons">
                    <div className="icon-container">{method.icon}</div>
                    <Plus size={16} className="plus-icon" />
                    <div className="icon-container">{method.secondIcon}</div>
                  </div>
                  <div className="method-info">
                    <h3>{method.title}</h3>
                    <p>{method.description}</p>
                  </div>
                  <div className="amount">
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    }).format(totalAmount)}
                  </div>
                  <AnimatePresence>
                    {selectedMethod === method.id && (
                      <motion.div
                        className="check-indicator"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                      />
                    )}
                  </AnimatePresence>
                </S.PaymentOption>
              ))}
            </S.OptionsRow>
          </S.PaymentOptionsGroup>
        )}
      </S.PaymentOptionsSection>
    </S.PaymentSelectorContainer>
  )
}

export default PaymentMethodSelector
